import React, { type FC, useMemo } from "react";

interface JsonSyntaxHighlighterProps {
  data: string | { [key: string]: any } | any[];
  showLineNumbers?: boolean;
  className?: string;
  style?: any;
}

/**
 * Simple JSON syntax highlighter for React 18
 * - Zero dependencies
 * - Works with JS objects or JSON strings
 * - Optional line numbers
 */
const JsonSyntaxHighlighter: FC<JsonSyntaxHighlighterProps> = ({
  data,
  showLineNumbers = false,
  className = "",
  style,
}) => {
  const jsonText = useMemo(() => {
    try {
      if (typeof data === "string") {
        // If it's a valid JSON string, pretty-print; otherwise highlight raw
        try {
          const parsed = JSON.parse(data);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return data; // highlight as-is
        }
      }
      // Objects/arrays
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data ?? "");
    }
  }, [data]);

  // Tokenize with a single regex pass.
  const tokens = useMemo(() => {
    const pattern =
      /"(?:\\.|[^"\\])*"(?=\s*:)|"(?:\\.|[^"\\])*"|\btrue\b|\bfalse\b|\bnull\b|-?\b\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?\b|[{}\[\],:]/g;

    const parts = [];
    let lastIndex = 0;

    jsonText.replace(pattern, (match, offset) => {
      if (lastIndex < offset) {
        parts.push({ type: "plain", value: jsonText.slice(lastIndex, offset) });
      }

      let type: string | undefined = "punct";
      if (/^"/.test(match)) {
        type = /:\s*$/.test(jsonText.slice(offset + match.length, offset + match.length + 1))
          ? "string"
          : /:\s*$/.test(match)
          ? "key"
          : undefined; // placeholder
      }

      // Keys are strings immediately followed by a colon (detected via lookahead in the regex)
      if (/^"(?:\\.|[^"\\])*"$/.test(match)) {
        // Determine if this string is a key by checking the original regex group via lookahead (done in pattern)
        // We can't directly see the lookahead result here, so re-test with an explicit check on the original text
        const after = jsonText.slice(offset + match.length);
        if (/^\s*:/.test(after)) {
          type = "key";
        } else {
          type = "string";
        }
      } else if (/true|false/.test(match)) {
        type = "boolean";
      } else if (/null/.test(match)) {
        type = "null";
      } else if (/^-?\d/.test(match)) {
        type = "number";
      } else if (/[{}\[\],:]/.test(match)) {
        type = "punct";
      }

      parts.push({ type, value: match });
      lastIndex = offset + match.length;
      return match;
    });

    if (lastIndex < jsonText.length) {
      parts.push({ type: "plain", value: jsonText.slice(lastIndex) });
    }

    return parts;
  }, [jsonText]);

  const lines = useMemo(() => jsonText.split("\n").length, [jsonText]);

  const typeToClass = (t: string) =>
    ({
      key: "text-sky-500",
      string: "text-emerald-500",
      number: "text-amber-500",
      boolean: "text-purple-500",
      null: "text-pink-500",
      punct: "text-zinc-400",
      plain: "text-zinc-200",
    }[t] || "text-zinc-200");

  // Inline fallback colors if Tailwind isn't present
  const typeToStyle = (t: string): React.CSSProperties =>
    ({
      key: { color: "#0ea5e9" },
      string: { color: "#10b981" },
      number: { color: "#f59e0b" },
      boolean: { color: "#8b5cf6" },
      null: { color: "#ec4899" },
      punct: { color: "#a1a1aa" },
      plain: { color: "#e4e4e7" },
    }[t] || { color: "#e4e4e7" });

  const containerBase = "w-full rounded-2xl bg-zinc-900 p-4 text-sm leading-6 font-mono overflow-auto shadow";

  if (!showLineNumbers) {
    return (
      <pre className={`${containerBase} ${className}`} style={style}>
        {tokens.map((tok, i) => (
          <span key={i} className={typeToClass(tok.type)} style={typeToStyle(tok.type)}>
            {tok.value}
          </span>
        ))}
      </pre>
    );
  }

  // With line numbers: render as a two-column grid
  const numbered = useMemo(() => {
    const text = tokens.map((t) => t.value).join("");
    const byLine = text.split("\n");

    // We need to rebuild tokens per line for coloring
    const lineSpans = [];
    let cursor = 0;

    for (let i = 0; i < byLine.length; i++) {
      const lineText = byLine[i];
      const end = cursor + lineText.length;
      const spans = [];
      let pos = cursor;
      for (const tok of tokens) {
        const tokStart = pos;
        // Skip tokens we've already consumed
        // We'll iterate linearly by slicing tokens across line boundaries
      }
      cursor = end + 1; // +1 for the newline
    }

    // Simpler approach: re-tokenize each line independently with the same regex
    const re =
      /"(?:\\.|[^"\\])*"(?=\s*:)|"(?:\\.|[^"\\])*"|\btrue\b|\bfalse\b|\bnull\b|-?\b\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?\b|[{}\[\],:]/g;

    for (const line of byLine) {
      const spans = [];
      let idx = 0;
      line.replace(re, (m, off) => {
        if (idx < off) spans.push({ type: "plain", value: line.slice(idx, off) });
        let type = "punct";
        if (/^"/.test(m)) {
          const after = line.slice(off + m.length);
          type = /^\s*:/.test(after) ? "key" : "string";
        } else if (/true|false/.test(m)) type = "boolean";
        else if (/null/.test(m)) type = "null";
        else if (/^-?\d/.test(m)) type = "number";
        else type = "punct";
        spans.push({ type, value: m });
        idx = off + m.length;
        return m;
      });
      if (idx < line.length) spans.push({ type: "plain", value: line.slice(idx) });
      lineSpans.push(spans);
    }

    return lineSpans;
  }, [tokens]);

  const totalDigits = String(lines).length;

  return (
    <div
      className={`${containerBase} ${className}`}
      style={{
        display: "grid",
        gridTemplateColumns: showLineNumbers ? "max-content 1fr" : "1fr",
        gap: "1rem",
        ...style,
      }}
    >
      <div
        className="text-right select-none pr-2 border-r border-zinc-700/50 text-zinc-500"
        style={{ paddingRight: 8 }}
      >
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} style={{ lineHeight: "1.5rem" }}>
            {String(i + 1).padStart(totalDigits, " ")}
          </div>
        ))}
      </div>
      <pre className="m-0">
        {numbered.map((line, i) => (
          <div key={i} style={{ lineHeight: "1.5rem" }}>
            {line.map((tok, j) => (
              <span key={`${i}-${j}`} className={typeToClass(tok.type)} style={typeToStyle(tok.type)}>
                {tok.value}
              </span>
            ))}
            {i < numbered.length - 1 ? "\n" : null}
          </div>
        ))}
      </pre>
    </div>
  );
};

export default JsonSyntaxHighlighter;
